import { useAuthStore } from '#modules/auth/store/auth';
import { useNotificationsStore } from '~/store/notifications';
import type { ApiInstance, ApiQueryParams, LaravelPaginatedResponse } from '~/types/api';
import type { Notification } from '~/types/Notification';

// Global state to track if auto-fetch is already initialized
const isAutoFetchInitialized = ref(false);
let intervalId: ReturnType<typeof setInterval> | null = null;

export const useNotification = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Initialize the API model
  const NotificationModel = useApiModel<Notification>('notifications');

  // Get notifications store - the single source of truth
  const store = useNotificationsStore();
  const route = useRoute();

  // Transform notification to resolve message placeholders and format messages
  function transformNotification(notification: Notification): Notification {
    const transformed = { ...notification };

    if (transformed.message && typeof transformed.message === 'string') {
      let message = transformed.message;

      // Replace :job placeholder with job title if available
      // API sends placeholders that need to be resolved
      if (message.includes(':job') && transformed.job) {
        const jobTitle = transformed.job.name || 'Job';
        const jobLocation = transformed.job.location_name || '';
        const locationText = jobLocation ? ` – ${jobLocation}` : '';
        // Wrap job title in <strong> for Figma highlighting
        message = message.replace(':job', `<strong>${jobTitle}</strong>`);
      }

      // Replace :actor placeholder with actor name if available (first name + last initial for Figma format)
      if (message.includes(':actor') && transformed.actor) {
        const firstName = transformed.actor.first_name || 'Someone';
        const lastName = transformed.actor.last_name;
        const actorName = lastName ? `${firstName} ${lastName[0]}.` : firstName;
        // Wrap actor name in <strong> for Figma highlighting
        message = message.replace(':actor', `<strong>${actorName}</strong>`);
      }

      // Replace :bid placeholder with bid amount if available
      if (message.includes(':bid') && transformed.bid) {
        const bidAmount = transformed.bid.rate ? `$${transformed.bid.rate}` : 'a bid';
        message = message.replace(':bid', bidAmount);
      }

      transformed.message = message;
    }

    // Preserve banner information if present in bid
    if (transformed.bid?.banner) {
      transformed.banner = transformed.bid.banner;
    }

    // Preserve role information from notifiable
    if (transformed.notifiable?.role) {
      transformed.userRole = transformed.notifiable.role;
    }

    return transformed;
  }

  async function getNotifications(params: ApiQueryParams = {}) {
    loading.value = true;
    error.value = null;

    try {
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 9,
        sort: params.sort || '-created_at',
        include: params.include || 'notifiable.avatar,actor.avatar,bid.user,job.user',
        ...params,
      };

      // Build query chain based on parameters
      const direction = queryParams.sort?.startsWith('-') ? 'desc' : 'asc';
      const field = queryParams.sort?.replace(/^-/, '') || 'created_at';

      // Chain all query methods in a single expression
      const response = (await NotificationModel
        // .where('search', params?.search || '')
        .orderBy(field, direction)
        .with(queryParams.include)
        .paginate(queryParams.limit, queryParams.page)) as LaravelPaginatedResponse<Notification>;

      const data = response.data.map(transformNotification);

      if (response.meta?.current_page === 1) {
        // First page - set initial notifications
        store.setNotifications(data);
      } else {
        // Subsequent pages - append to existing notifications
        store.updateNotificationList(data);
      }

      // Update pagination state in store
      store.updatePagination({
        currentPage: response.meta?.current_page || 1,
        lastPage: response.meta?.last_page || 1,
        total: response.meta?.total || 0,
        perPage: response.meta?.per_page || queryParams.limit,
      });
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch notifications';
      console.error('Error fetching notifications:', err);
    } finally {
      loading.value = false;
    }
  }

  async function updateNotification(
    notificationId: number | string,
    updates: Partial<Notification> | undefined
  ): Promise<void> {
    let payload = { ...updates } as Partial<Notification>;
    if (!payload.hasOwnProperty('notification_id')) {
      payload['notification_id'] = notificationId;
    }

    const { $apiClient } = useNuxtApp() as unknown as { $apiClient: ApiInstance };

    await $apiClient.put('notifications', payload);
  }

  async function loadMoreNotifications(): Promise<void> {
    if (store.pagination.currentPage < store.pagination.lastPage) {
      const nextPage = store.pagination.currentPage + 1;
      await getNotifications({ page: nextPage, limit: store.pagination.perPage });
    } else {
      console.log('No more notifications to load.');
    }
  }

  // Auto-fetch notifications on route changes and periodically
  function initAutoFetch() {
    // Skip if already initialized
    if (isAutoFetchInitialized.value) {
      return;
    }

    isAutoFetchInitialized.value = true;

    const authStore = useAuthStore();

    // Fetch notifications function with error handling
    const fetchNotifications = async () => {
      // Skip fetching if user is not logged in
      if (!authStore.hasLoggedInUser) {
        return;
      }

      // Skip fetching if drawer is open (user is actively viewing notifications)
      if (store.isDrawerOpen) {
        return;
      }

      try {
        await getNotifications({ page: 1, limit: 9 });
      } catch (err) {
        console.error('Failed to auto-fetch notifications:', err);
      }
    };

    // Initial fetch on mount
    onMounted(() => {
      fetchNotifications();
    });

    // Watch for auth state changes - fetch when user logs in
    watch(
      () => authStore.hasLoggedInUser,
      (isLoggedIn) => {
        if (isLoggedIn) {
          fetchNotifications();
        }
      },
      { immediate: true } // Check immediately on mount
    );

    // Watch for route changes
    watch(
      () => route.fullPath,
      () => {
        fetchNotifications();
      }
    );

    // Set up 3-minute interval polling
    onMounted(() => {
      intervalId = setInterval(
        () => {
          fetchNotifications();
        },
        3 * 60 * 1000
      ); // 3 minutes
    });

    // Cleanup interval on unmount
    onUnmounted(() => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      isAutoFetchInitialized.value = false;
    });
  }

  return {
    loading,
    error,

    // Expose store notifications and pagination for convenience
    notifications: store.notifications,
    pagination: store.pagination,

    getNotifications,
    transformNotification,

    // Pop-up notification methods
    updateNotification,
    loadMoreNotifications,

    // Auto-fetch initialization
    initAutoFetch,
  };
};
